import "./UnifyCard.scss";
import React, { useState } from "react";
import { faHardDrive, faServer, faSignalStream } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    AnyMapper,
    MappingView,
    MixerMapperComp,
    isEdgeMapper,
} from "userful-chronos-app-common-js/dist/models/mapping/MappingGroups";
import { useTranslation } from "react-i18next";
import { getGlobalStates, isGCM } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { UserPermission } from "userful-chronos-app-common-js/dist/models/user-permission";
import UserfulTooltip from "../UserfulTooltip";
import Form from "react-bootstrap/Form";
import MapperHLSStreamDialog from "../../Stream/MapperHLSStreamDialog";
import { APP_IDS, UnifyCardType } from "userful-chronos-app-common-js/dist/models/common";
import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";
import { anyDeleteExecuteActionAllowed } from "userful-chronos-app-common-js/dist/authenticataion/roles";
import Checkmark from "../../Checkmark/Checkmark";

interface IProps {
    title: string; // name shown on tooltip
    view?: MappingView; // margin below the whole card.  MappingView = "Detailed" | "Minimized". 8px or 16px depending on the type
    id?: string; // check box id
    onClick?: Function; // when checkbox is clicked
    name: string; // name to be displayed
    checked?: boolean; // boolean value for check box
    type: UnifyCardType; //  UnifyCardType = "MappingGroup" | "Multiwindow" | "Channel" | "MeetingSpace"
    mappingGroup?: AnyMapper;
    onClickBody?: Function; // when clicked on body div containing name and description
    appID: string; // appID where component is being used
    mappingGroupMenu?: React.ReactNode; // specifically for mapping group actions button
    zIndex?: string; // zIndex for the mapper
    streamCount?: number; // userd in ruby for displaying the count of streams
    description?: string; // description of the component
    unsetMapperCardWidth?: boolean; // default value for mapper is 240px. Sould be true if the fixed width is not required
    onClickComponent?: Function; // when whole card is clicked
    spacesGuest?: React.ReactNode; // specifically for displaying guest
    sapphireLink?: string;
    channelSingleMenu?: React.ReactNode; // specifically for channel dropdown menu
}

const UnifyCard = (props: IProps) => {
    const { t } = useTranslation();
    const userPermission = getGlobalStates().userPermission as UserPermission;
    const getClassName = (type: UnifyCardType) => {
        if (["MappingGroup", "Multiwindow"].includes(type)) return "purpleDiv";
        if (type === "Channel") return "redDiv";
        if (type === "MeetingSpace") return "lgblueDiv";
    };

    const allowedIDsMapper = [APP_IDS.Artistic, APP_IDS.Engage, APP_IDS.Spaces, APP_IDS.Trends];

    const handleOpenURL = (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(props.sapphireLink);
    };

    return (
        <div
            className={`mappingCard ${(props.type === "Channel" || props.type === "MeetingSpace") && "channelTypeHover"} ${props.checked ? "mappingCardSelected" : ""}`}
            onClick={() => props.onClickComponent && props.onClickComponent()}
        >
            <div className="header">
                <div className="cardLabel">
                    {props.onClick && (
                        <Checkmark
                            selected={props.checked ? "selected" : "unselected"}
                            onClick={(e) => props.onClick(!props.checked)}
                            boxArround
                        />
                    )}
                    <div className={`cardType ${getClassName(props.type)}`}>{t(`Shared.${props.type}`)}</div>
                    {allowedIDsMapper.includes(props.appID) && (
                        <UserfulTooltip
                            title={
                                isEdgeMapper(props.mappingGroup)
                                    ? t("CommonUI.MappingGroups.EdgePlayer")
                                    : t("CommonUI.MappingGroups.ServerPlayer")
                            }
                        >
                            <div className="mapperType">
                                {isEdgeMapper(props.mappingGroup) && <FontAwesomeIcon icon={faHardDrive} />}
                                {!isEdgeMapper(props.mappingGroup) && <FontAwesomeIcon icon={faServer} />}
                            </div>
                        </UserfulTooltip>
                    )}
                    {props.mappingGroup && !isEdgeMapper(props.mappingGroup) && (
                        <MapperHLSStreamDialog
                            iconOnly
                            mapper={props.mappingGroup as MixerMapperComp}
                            disabled={(props.mappingGroup as MixerMapperComp).mixerPlayerActivationStatus !== "ACTIVE"}
                            unifyCardStreaming
                        />
                    )}

                    {props.streamCount !== undefined && props.streamCount !== null && (
                        <UserfulTooltip title={`${props.streamCount} streams`}>
                            <div className="streamCount">
                                <div>
                                    <FontAwesomeIcon icon={faSignalStream} />
                                </div>
                                <div> {props.streamCount} </div>
                            </div>
                        </UserfulTooltip>
                    )}
                    {props.appID === APP_IDS.Spaces && props.spacesGuest && props.spacesGuest}
                </div>

                <div className="inRowIcons">
                    {props.appID === APP_IDS.Spaces && !isGCM() && (
                        <UserfulTooltip title={t("SapphireApp.JoinSpace")}>
                            <div className="spacesLinkIconDiv" onClick={handleOpenURL}>
                                <FontAwesomeIcon icon={faExternalLink} className="spacesLinkIcon" />
                            </div>
                        </UserfulTooltip>
                    )}
                    {props.mappingGroupMenu && (
                        <div className="cardAction">
                            {(anyDeleteExecuteActionAllowed(props.mappingGroup.userfulSecurityData) ||
                                userPermission.canOverrideSecurityDataOwnerShip ||
                                props.appID === APP_IDS.Spaces) &&
                                props.mappingGroupMenu}
                        </div>
                    )}
                    {props.channelSingleMenu && <div className="cardAction">{props.channelSingleMenu}</div>}
                </div>
            </div>
            {userPermission.isMapperUpdater ? (
                <div
                    className={`mapperBody ${props.onClickBody && "channelTypeHover"}`}
                    onClick={() => props?.onClickBody()}
                >
                    <UserfulTooltip placement="bottom" title={props.title}>
                        <div className="mapperName">{props.name}</div>
                    </UserfulTooltip>
                    {props.mappingGroup && <div className="mapperDescription">{props.mappingGroup.description}</div>}
                    {props.description && <div className="mapperDescription">{props.description}</div>}
                </div>
            ) : (
                <div
                    className={`mapperBody ${props.onClickBody && "channelTypeHover"}`}
                    onClick={() => props?.onClickBody()}
                >
                    <UserfulTooltip placement="bottom" title={props.title}>
                        <div className="mapperName">{props.name}</div>
                    </UserfulTooltip>
                    {props.mappingGroup && <div className="mapperDescription">{props.mappingGroup.description}</div>}
                    {props.description && <div className="mapperDescription">{props.description}</div>}
                </div>
            )}
        </div>
    );
};

export default UnifyCard;
